import { isPlatformBrowser } from '@angular/common';
import { EnvironmentProviders, InjectionToken, PLATFORM_ID, Provider, inject } from '@angular/core';
import {
  ApolloClientOptions,
  ApolloLink,
  Observable as ApolloObservable,
  FetchResult,
  FieldPolicy,
  InMemoryCache,
  NextLink,
  NormalizedCacheObject,
  Operation,
  fromPromise,
  split,
} from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { Reference, getMainDefinition } from '@apollo/client/utilities';
import { I18NEXT_SERVICE, ITranslationService } from 'angular-i18next';
import { APOLLO_OPTIONS } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { Body, ExtractFiles } from 'apollo-angular/http/types';
import { createPersistedQueryLink } from 'apollo-angular/persisted-queries';
import { Kind, OperationTypeNode, print } from 'graphql';
import { CryptoHashService } from 'src/app/common/crypto-hash/crypto-hash.service';
import { GraphqlStateService } from 'src/app/common/graphql-state/graphql-state.service';
import { environment } from 'src/environments/environment';
import { FavouriteLanguage, Post } from '../src/types/graphql';
import introspectionQueryResultData from '../src/types/possible-types';
import { hashCode } from './hashCode';

const isExtractable = (value: any) =>
  (typeof File !== 'undefined' && value instanceof File) || (typeof Blob !== 'undefined' && value instanceof Blob);

export const extractFiles: ExtractFiles = (body: Body | Body[]) => {
  const clones = new Map();

  const files = new Map();

  const recurse = (value: Body | Body[], path: string, recursed: Set<any>) => {
    if (isExtractable(value)) {
      const filePaths = files.get(value);
      if (filePaths) {
        filePaths.push(value);
      } else {
        files.set(value, [path]);
      }
      return null;
    }

    const valueIsList = Array.isArray(value) || (typeof FileList !== 'undefined' && value instanceof FileList);
    const valueIsPlainObject = isPlainObject(value);

    if (valueIsList || valueIsPlainObject) {
      let clone = clones.get(value);

      const uncloned = !clone;

      if (uncloned) {
        clone = valueIsList ? [] : value instanceof Object ? {} : Object.create(null);

        clones.set(value, clone);
      }

      if (!recursed.has(value)) {
        const pathPrefix = path ? `${path}.` : '';
        const recursedDeeper = new Set(recursed).add(value);

        if (valueIsList) {
          let index = 0;

          for (const item of value as Body[]) {
            const itemClone = recurse(item, pathPrefix + index++, recursedDeeper);

            if (uncloned) clone.push(itemClone);
          }
        } else {
          // eslint-disable-next-line guard-for-in
          for (const key in value) {
            const propertyClone = recurse((value as any)[key], pathPrefix + key, recursedDeeper);
            if (uncloned) clone[key] = propertyClone;
          }
        }
      }

      return clone;
    }

    return value;
  };

  return {
    clone: recurse(body, '', new Set()),
    files,
  };
};

interface Definintion {
  kind: Kind.OPERATION_DEFINITION | Kind.FRAGMENT_DEFINITION;
  operation?: OperationTypeNode;
}

export const isPlainObject = (value: any) => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return (
    (prototype === null || prototype === Object.prototype || Object.getPrototypeOf(prototype) === null) &&
    !(Symbol.toStringTag in value) &&
    !(Symbol.iterator in value)
  );
};

type RequestResult = FetchResult<{ [key: string]: any }, Record<string, any>, Record<string, any>>;

type KeyArgs = FieldPolicy<any>['keyArgs'];
export const offsetLimitPagination = <T = Reference>(keyArgs: KeyArgs = false): FieldPolicy<T[]> => ({
  keyArgs,
  read: (existing, { args }) => existing && existing.slice(0),
  merge(existing, incoming, { args }) {
    const merged = existing ? existing.slice(0) : [];
    if (incoming) {
      if (args) {
        // Assume a skip of 0 if args.skip omitted.
        const skip = args.skip || args.skipSearch || 0;
        for (let i = 0; i < incoming.length; ++i) {
          merged[skip + i] = incoming[i];
        }
      } else {
        // It's unusual (probably a mistake) for a paginated field not
        // to receive any arguments, so you might prefer to throw an
        // exception here, instead of recovering by appending incoming
        // onto the existing array.
        // eslint-disable-next-line prefer-spread
        merged.push.apply(merged, incoming as T[]);
      }
    }
    return merged;
  },
});

const APOLLO_CACHE = new InjectionToken<InMemoryCache>('apollo-cache');

export const APOLLO_PROVIDERS: (Provider | EnvironmentProviders)[] = [
  {
    provide: APOLLO_CACHE,
    useValue: new InMemoryCache({
      addTypename: true,
      possibleTypes: introspectionQueryResultData.possibleTypes,
      typePolicies: {
        Query: {
          fields: {
            allSearchableContents: offsetLimitPagination((args, _context) => {
              const variableArgs = { ...args };
              delete variableArgs.skip;
              delete variableArgs.limit;
              return hashCode(JSON.stringify(variableArgs));
            }),
          },
        },
        Land: {
          fields: {
            postList: offsetLimitPagination<Post>(['$filter']),
            calendarList: offsetLimitPagination(['$filter']),
            featuredList: offsetLimitPagination(['$filter']),
            userGoldList: offsetLimitPagination(['$filter']),
          },
        },
        MaintenanceSite: {
          keyFields: [],
        },
        Image: {
          keyFields: ['id'],
        },
        AuthHeaderData: {
          keyFields: ['uid'],
        },
        BillingInfo: {
          keyFields: ['email', 'fiscalCode'],
        },
        LastTranslationCommit: {
          keyFields: ['createdAt', 'userName'],
        },
        OperationReturn: {
          keyFields: [],
        },
        PaymentIntentData: {
          keyFields: ['paymentIntentId', 'clientSecret'],
        },
        PrivacyPolicyVersion: {
          keyFields: ['version'],
        },
        SearchNode: {
          keyFields: [],
        },
        TermsAndConditionsVersion: {
          keyFields: ['version'],
        },
        Translation: {
          keyFields: [],
        },
        UserSignIn: {
          keyFields: ['user', ['id']],
        },
        Visitor: {
          keyFields: [],
        },
        AveragePageVisits: {
          keyFields: [],
        },
        AveragePageVisitTime: {
          keyFields: [],
        },
        BounceRate: {
          keyFields: [],
        },
        LikeCollection: {
          fields: {
            likeCollectionContents: {
              merge: (_existing, incoming) => incoming,
            },
          },
        },
      },
    }),
  },
  {
    provide: APOLLO_OPTIONS,
    useFactory: (
      httpLink: HttpLink,
      cache: InMemoryCache,
      platformId: NonNullable<unknown>,
      translateService: ITranslationService,
    ): ApolloClientOptions<NormalizedCacheObject> => {
      const graphqlStateService = inject(GraphqlStateService);
      const sha256 = inject(CryptoHashService).sha256;
      const isBrowser = isPlatformBrowser(platformId);
      const persist = createPersistedQueryLink({
        sha256,
        useGETForHashedQueries: false,
      });

      const httpGet = httpLink.create({
        uri: environment.graphqlEndpoint,
        withCredentials: true,
        useMultipart: false,
        useGETForQueries: false,
      });

      const httpPost = httpLink.create({
        uri: environment.graphqlEndpoint,
        withCredentials: true,
        useMultipart: true,
        extractFiles,
      });

      const http = split(
        ({ query, operationName }) => {
          const { kind, operation }: Definintion = getMainDefinition(query);
          return kind === 'OperationDefinition' && operation === 'query' && !['fileExif'].includes(operationName);
        },
        persist.concat(httpGet),
        httpPost,
      );

      const error = onError(({ graphQLErrors, networkError, operation }) => {
        if (['initUser', 'me', 'canActivate'].includes(operation?.operationName)) return;
        console.error(`operation '${operation.operationName}',\nerror: `, { graphQLErrors, networkError });
      });

      const cacheLink = new ApolloLink(
        operation =>
          new ApolloObservable(s => {
            s.next(graphqlStateService.getFromState(operation.query, operation.variables));
            s.complete();
          }),
      );

      const middle = new ApolloLink((operation, forward) => {
        const ctx = operation.getContext();
        const locale = translateService.language || FavouriteLanguage.It;
        const context = {
          ...ctx,
          headers: {
            ['cache-version']: '1',
            locale,
          },
        };
        operation.setContext(context);
        return forward(operation).map(response => {
          const context = operation.getContext();
          const {
            response: { headers },
          } = context;
          const newRes = { ...response };
          if (headers) {
            const total = headers.get('total');
            const total_dashboard = headers.get('total_dashboard');
            if (total) {
              newRes.data.total = total;
            }
            if (total_dashboard) {
              newRes.data.total_dashboard = JSON.parse(total_dashboard);
            }
          }
          if (!isBrowser) {
            graphqlStateService.setInState(operation.query, operation.variables, newRes);
          }
          return newRes;
        });
      });
      const link = split(
        ({ query, operationName, variables }) => {
          const { kind, operation }: Definintion = getMainDefinition(query);
          return (
            kind === 'OperationDefinition' &&
            operation === 'query' &&
            !['fileExif', 'cartItemsCount', 'initUser', 'me', 'postLikes'].includes(operationName) &&
            graphqlStateService.hasState(query, variables)
          );
        },
        cacheLink,
        error.concat(middle).concat(http),
      );

      return {
        credentials: 'include',
        ...(!isBrowser
          ? { link }
          : {
              link: split(
                // split based on operation type
                ({ query }) => {
                  const { kind, operation }: Definintion = getMainDefinition(query);
                  return kind === 'OperationDefinition' && operation === 'subscription';
                },
                new SharryActionCableLink(),
                link,
              ),
            }),
        cache,
        ...(isBrowser
          ? {
              // queries with `forceFetch` enabled will be delayed
              ssrForceFetchDelay: 100,
            }
          : {
              // avoid to run twice queries with `forceFetch` enabled
              ssrMode: true,
            }),
      } as ApolloClientOptions<NormalizedCacheObject>;
    },
    deps: [HttpLink, APOLLO_CACHE, PLATFORM_ID, I18NEXT_SERVICE],
  },
];

class SharryActionCableLink extends ApolloLink {
  constructor() {
    super();
  }

  // Interestingly, this link does _not_ call through to `next` because
  // instead, it sends the request to ActionCable.
  request(operation: Operation, _next: NextLink): ApolloObservable<RequestResult> {
    return fromPromise(import('actioncable')).flatMap(({ default: actioncable }) => {
      const observable = new ApolloObservable(observer => {
        const channelId = Math.round(Date.now() + Math.random() * 100000).toString(16);
        const actionName = 'execute';
        // (actioncable as any).startDebugging();
        const consumer = actioncable.createConsumer(environment.websocketEndpoint);
        const channel = consumer.subscriptions.create(
          {
            channel: 'GraphqlChannel',
            channelId,
          },
          {
            connected: () => {
              channel.perform('subscribed', {});
              channel.perform(actionName, {
                query: operation.query ? print(operation.query) : null,
                variables: operation.variables,
                // This is added for persisted operation support:
                operationId: (operation as { operationId?: string }).operationId,
                operationName: operation?.operationName,
              });
            },
            disconnected: () => {
              // this does nothing
            },
            received: payload => {
              if (payload?.result?.data || payload?.result?.errors) {
                observer.next(payload.result);
              }
              if (!payload?.more) {
                observer.complete();
              }
            },
            unsubscribe: () => {
              observer.complete();
              channel.perform('unsubscribed', {});
              consumer.disconnect();
            },
          },
        );
        // Make the ActionCable subscription behave like an Apollo subscription
        return Object.assign(channel, { closed: false });
      });
      return observable;
    });
  }
}
